import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Auth türleri
export interface User {
  username: string;
  isAdmin: boolean;
  loginTime: number;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

// Context oluştur
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Basit kullanıcı veritabanı (gerçek uygulamada backend'den gelir)
const VALID_CREDENTIALS = {
  admin: 'admin123',
  emre: 'emre2025',
  bot_admin: 'BotManager!23'
};

// Session süre sınırı (24 saat)
const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 saat milisaniye

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sayfa yüklendiğinde local storage'dan kullanıcıyı kontrol et
  useEffect(() => {
    const checkStoredAuth = () => {
      try {
        const storedUser = localStorage.getItem('bot_panel_user');
        if (storedUser) {
          const userData: User = JSON.parse(storedUser);
          
          // Session süresini kontrol et
          const now = Date.now();
          const sessionAge = now - userData.loginTime;
          
          if (sessionAge < SESSION_DURATION) {
            setUser(userData);
          } else {
            // Session süresi dolmuş, temizle
            localStorage.removeItem('bot_panel_user');
          }
        }
      } catch (error) {
        console.error('Auth kontrol hatası:', error);
        localStorage.removeItem('bot_panel_user');
      } finally {
        setIsLoading(false);
      }
    };

    checkStoredAuth();
  }, []);

  // Giriş fonksiyonu
  const login = async (username: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // Simüle edilmiş gecikme (gerçek API çağrısı için)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Kullanıcı adı ve şifre kontrolü
      if (VALID_CREDENTIALS[username as keyof typeof VALID_CREDENTIALS] === password) {
        const newUser: User = {
          username,
          isAdmin: true, // Tüm kullanıcılar admin (basit versiyon)
          loginTime: Date.now(),
        };

        setUser(newUser);
        localStorage.setItem('bot_panel_user', JSON.stringify(newUser));
        return true;
      } else {
        setError('Geçersiz kullanıcı adı veya şifre');
        return false;
      }
    } catch (error) {
      setError('Giriş sırasında bir hata oluştu');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Çıkış fonksiyonu
  const logout = () => {
    setUser(null);
    setError(null);
    localStorage.removeItem('bot_panel_user');
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Protected Route component
interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.9)',
          padding: '30px',
          borderRadius: '20px',
          textAlign: 'center',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        }}>
          <div className="loading-spinner" style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            margin: '0 auto 15px',
          }}></div>
          <div style={{ color: '#667eea', fontWeight: 'bold' }}>Yükleniyor...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Login form gösterilecek
  }

  return <>{children}</>;
};
